import React, { useEffect, useMemo, useState } from "react";
import { RiWhatsappFill } from "react-icons/ri";
import { Link, NavLink } from "react-router-dom";

import { getWhatsAppHref } from "../config/contactChannels";
import { useApi } from "../contexts/ApiProvider";
import { useFlash } from "../contexts/FlashProvider";
import {
  fallbackServices,
  normalizeService,
} from "../helpers/serviceFallbacks";
import { resolveCmsAsset } from "../cms/media";
import { useCmsGlobal } from "../cms/useCmsContent";

const navLinkClass = ({ isActive }) =>
  `nav-link ${isActive ? "active" : ""}`;

export default function Navbar() {
  const api = useApi();
  const flash = useFlash();
  const { globalContent } = useCmsGlobal();
  const navbar = globalContent.navbar || {};
  const contact = globalContent.contact || {};
  const fallbackMenuServices = useMemo(() => fallbackServices.slice(0, 5), []);
  const [services, setServices] = useState(fallbackMenuServices);
  const whatsappHref = contact.whatsapp
    ? `https://wa.me/${String(contact.whatsapp).replace(/\D/g, "")}`
    : getWhatsAppHref();

  useEffect(() => {
    (async () => {
      const response = await api.get("/api/services/search");
      if (response.success && response?.services?.length) {
        setServices(response.services.slice(0, 5).map(normalizeService));
      } else if (response.error) {
        flash(response.error?.message || "Services indisponibles", "warning");
      }
    })();
  }, [api, flash]);

  return (
    <nav className="navbar site-navbar navbar-expand-lg navbar-light bg-white border-bottom sticky-top">
      <div className="container">
        <Link
          className="navbar-brand d-flex align-items-center lh-1 me-lg-6"
          to="/"
          aria-label="Fatihoune - accueil"
        >
          <img
            className="site-navbar-logo"
            src={resolveCmsAsset(navbar.logo, "/assets/images/logo.png")}
            alt="Fatihoune"
          />
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#mainNavigation"
          aria-controls="mainNavigation"
          aria-expanded="false"
          aria-label="Ouvrir la navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div
          className="collapse navbar-collapse justify-content-between align-items-lg-center"
          id="mainNavigation"
        >
          <ul className="navbar-nav align-items-lg-center">
            <li className="nav-item dropdown">
              <button
                className="nav-link dropdown-toggle btn btn-link"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Services
              </button>
              <div className="dropdown-menu service-dropdown shadow-sm">
                <div className="service-dropdown-inner">
                  {services.map((service) => (
                    <Link
                      className="service-dropdown-item"
                      key={service._id || service.slug}
                      to={`/services/${service.slug}`}
                    >
                      <span className="service-dropdown-title">
                        {service.title}
                      </span>
                      <span className="service-dropdown-text">
                        {service.description}
                      </span>
                    </Link>
                  ))}
                  <div className="service-dropdown-footer">
                    <Link className="fw-bold text-decoration-none" to="/services">
                      Voir tous les services <span aria-hidden="true">&rarr;</span>
                    </Link>
                  </div>
                </div>
              </div>
            </li>
            <li className="nav-item">
              <NavLink to="/about" className={navLinkClass}>
                Qui sommes-nous
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/realisations" className={navLinkClass}>
                Réalisations
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/documents" className={navLinkClass}>
                Documents
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/contact" className={navLinkClass}>
                Contact
              </NavLink>
            </li>
          </ul>

          <div className="d-flex flex-column flex-lg-row gap-2 mt-4 mt-lg-0">
            <a
              href={whatsappHref}
              className="btn btn-whatsapp whatsapp-icon-link"
              target="_blank"
              rel="noreferrer"
              aria-label="Contacter Fatihoune sur WhatsApp"
              title="WhatsApp"
            >
              <RiWhatsappFill aria-hidden="true" />
              <span className="visually-hidden">WhatsApp</span>
            </a>
            <Link to="/contact" className="btn btn-success">
              {navbar.ctaLabel || "Recevoir une proposition"}
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
